<template>
  <div class="api-keys-container">
    <div class="container-fluid">
      <!-- Header -->
      <div class="row mb-4">
        <div class="col-12">
          <h2 class="mb-0">
            <i class="fa fa-key"></i> API Key 관리
          </h2>
          <p class="text-muted">External API 접근을 위한 API Key를 관리합니다.</p>
        </div>
      </div>

      <!-- API Key Management Section -->
      <div class="row mb-4">
        <div class="col-12">
          <div class="card">
            <div class="card-header d-flex justify-content-between align-items-center">
              <h5 class="mb-0">
                <i class="fa fa-list"></i> 내 API Keys
              </h5>
              <button @click="createApiKey" class="btn btn-sm btn-primary">
                <i class="fa fa-plus"></i> 새 API Key 발급
              </button>
            </div>
            <div class="card-body">
              <div v-if="loading" class="text-center py-4">
                <div class="spinner-border text-primary" role="status">
                  <span class="sr-only">Loading...</span>
                </div>
              </div>
              <div v-else-if="apiKeys.length === 0" class="text-center text-muted py-4">
                <i class="fa fa-key fa-3x mb-3"></i>
                <p>발급된 API Key가 없습니다.</p>
              </div>
              <div v-else class="table-responsive">
                <table class="table table-striped table-hover">
                  <thead class="thead-dark">
                    <tr>
                      <th>Key ID</th>
                      <th>Key Prefix</th>
                      <th>생성일</th>
                      <th>마지막 사용</th>
                      <th>총 요청수</th>
                      <th>관리</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="key in apiKeys" :key="key.key_id">
                      <td><code>{{ key.key_id }}</code></td>
                      <td><code>{{ key.key_prefix }}...</code></td>
                      <td>{{ formatDate(key.created_at) }}</td>
                      <td>{{ key.last_used_at ? formatDate(key.last_used_at) : '사용 기록 없음' }}</td>
                      <td>{{ key.total_requests }}</td>
                      <td>
                        <button @click="revokeApiKey(key.key_id)" class="btn btn-sm btn-outline-danger">
                          <i class="fa fa-trash"></i> 삭제
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- API Documentation Section -->
      <div class="row mb-4">
        <div class="col-12">
          <div class="card">
            <div class="card-header">
              <h5 class="mb-0">
                <i class="fa fa-book"></i> API 사용 가이드
              </h5>
            </div>
            <div class="card-body text-center py-4">
              <p class="mb-3">API 엔드포인트, 요청/응답 형식, 인증 방법 등 자세한 사용법은 API 문서를 참조하세요.</p>
              <a :href="swaggerUrl" target="_blank" class="btn btn-primary">
                <i class="fa fa-external-link"></i> API 문서 보기 (Swagger)
              </a>
            </div>
          </div>
        </div>
      </div>

      <!-- Create API Key Modal -->
      <div v-if="showCreateKeyModal" class="modal fade show" style="display: block; position: fixed; top: 0; left: 0; width: 100%; height: 100%; z-index: 1050;" tabindex="-1" role="dialog">
        <div class="modal-dialog modal-dialog-centered" role="document">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">
                <i class="fa fa-key"></i> 새 API Key 발급
              </h5>
              <button type="button" class="close" @click="closeCreateKeyModal">
                <span>&times;</span>
              </button>
            </div>
            <div class="modal-body">
              <div class="alert alert-warning">
                <i class="fa fa-exclamation-triangle"></i>
                <strong>중요!</strong> 이 API Key는 다시 조회할 수 없습니다. 안전한 곳에 저장하세요.
              </div>
              <div class="form-group">
                <label>Key ID</label>
                <input type="text" class="form-control" :value="newKeyId" readonly>
              </div>
              <div class="form-group mt-3">
                <label>API Key</label>
                <div class="input-group">
                  <input
                    type="text"
                    class="form-control"
                    :value="newApiKey"
                    readonly
                  >
                  <div class="input-group-append">
                    <button class="btn btn-outline-secondary" @click="copyApiKey">
                      <i class="fa fa-copy"></i> 복사
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" @click="closeCreateKeyModal">
                닫기
              </button>
            </div>
          </div>
        </div>
      </div>
      <div v-if="showCreateKeyModal" class="modal-backdrop fade show" style="position: fixed; top: 0; left: 0; width: 100%; height: 100%; z-index: 1040;"></div>

    </div>
  </div>
</template>

<script>
import axios from '@/libs/axios'

export default {
  name: 'ApiKeys',
  data() {
    return {
      loading: true,
      apiKeys: [],
      showCreateKeyModal: false,
      newKeyId: '',
      newApiKey: null
    }
  },
  mounted() {
    this.loadApiKeys()
  },
  computed: {
    swaggerUrl() {
      // Localstack 환경에서는 webserver로 직접 연결 (dev port: 15000)
      if (this.$store.state.info.isLocalstack) {
        return 'http://localhost:15000/external-api/'
      }
      return '/external-api/'
    }
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return 'N/A'
      const date = new Date(dateString)
      return date.toLocaleDateString('ko-KR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    async loadApiKeys() {
      this.loading = true
      try {
        const response = await axios.get('/api/keys/')
        if (response.data.success) {
          this.apiKeys = response.data.keys
        }
      } catch (error) {
        console.error('Failed to load API keys:', error)
      } finally {
        this.loading = false
      }
    },

    async createApiKey() {
      try {
        const response = await axios.post('/api/keys/')
        if (response.data.success) {
          this.newApiKey = response.data.api_key
          this.newKeyId = response.data.key_id
          this.showCreateKeyModal = true
          this.$toastr.success('API Key가 발급되었습니다.')
          await this.loadApiKeys()
        } else {
          this.$toastr.error(response.data.message || 'API Key 발급에 실패했습니다.')
        }
      } catch (error) {
        console.error('Failed to create API key:', error)
        const errorMessage = (error.response && error.response.data && error.response.data.message) || 'API Key 발급 중 오류가 발생했습니다.'
        this.$toastr.error(errorMessage)
      }
    },

    async revokeApiKey(keyId) {
      if (!confirm('이 API Key를 삭제하시겠습니까? 삭제된 키는 더 이상 사용할 수 없습니다.')) {
        return
      }

      try {
        const response = await axios.delete(`/api/keys/${keyId}`)
        if (response.data.success) {
          this.$toastr.success('API Key가 삭제되었습니다.')
          await this.loadApiKeys()
        } else {
          this.$toastr.error(response.data.message || 'API Key 삭제에 실패했습니다.')
        }
      } catch (error) {
        console.error('Failed to revoke API key:', error)
        this.$toastr.error('API Key 삭제 중 오류가 발생했습니다.')
      }
    },

    closeCreateKeyModal() {
      this.showCreateKeyModal = false
      this.newKeyId = ''
      this.newApiKey = null
    },

    copyApiKey() {
      if (this.newApiKey) {
        navigator.clipboard.writeText(this.newApiKey).then(() => {
          this.$toastr.success('API Key가 클립보드에 복사되었습니다.')
        }).catch(() => {
          this.$toastr.error('복사에 실패했습니다. 수동으로 복사해주세요.')
        })
      }
    }
  }
}
</script>

<style scoped>
.api-keys-container {
  padding: 20px;
  margin-top: 56px;
}
</style>
